import { getSkillsForSection, skillsReady } from '../../skills/loader.js';
import { claude, requireKey, skill } from '../api.js';
import { SECTION_TOKENS_DEFAULT } from '../constants.js';
import {
  assembledForPage,
  briefTxt,
  finalizeFrame,
  patchFrame,
  showErr,
  showToast,
  writeFrame,
} from '../dom.js';
import {
  buildBusinessContext,
  buildImageContext,
  buildTokenContext,
  resolveLogoSrc,
} from '../prompts/context.js';
import { SECTION_TYPE_RULES } from '../prompts/rules.js';
import { getSectionModePrompt } from '../prompts/section.js';
import { renderChat } from '../screens/chat.js';
import { renderSbFoot, renderShellRow, renderSList, updSectsCtx } from '../screens/sections.js';
import { curPage, ST } from '../state.js';
import { buildDesignShell } from './design.js';
import { injectFormMeta } from './export.js';
import {
  buildUploadImgMap,
  cleanModelOutput,
  clearImgFetchCache,
  collectSectionImages,
  getLogoVisionInput,
  safeStreamHtml,
  stripHtmlToText,
} from './helpers.js';

// ── BUILD PIPELINE ────────────────────────────────────────────────
// Section-by-section build orchestration, streaming, assembly, sync.

// ── PIPELINE ORCHESTRATION (mixed: API + DOM) ── API calls -> composable, DOM renders -> Vue reactivity.

/**
 * Start the full-page build pipeline (all sections).
 * @returns {void}
 */
function startBuildAll() {
  const pg = ST.pages[ST.activePage];
  const hasFormSect = pg.sects.some(s => s.type === 'contact' || s.type === 'subscribe');
  if (hasFormSect && !ST.notificationEmail) {
    const email = prompt('Enter your notification email for form submissions:');
    if (!email?.includes('@')) {
      showErr('A valid notification email is required for form sections.');
      return;
    }
    ST.notificationEmail = email.trim();
    localStorage.setItem('sf_notify_email', ST.notificationEmail);
  }
  requireKey(() => {
    void buildAll();
  });
}

async function buildAll() {
  if (ST.running) {
    return;
  }
  // Ensure skills are loaded before building (usually instant — fetches start on page load)
  await skillsReady;

  ST.running = true;
  clearImgFetchCache();
  const pgIdx = ST.activePage;
  const pg = ST.pages[pgIdx];
  renderSbFoot();
  renderChat();
  if (pg.shellStatus !== 'done') {
    pg.shellStatus = 'building';
    renderShellRow();
    updSectsCtx();
    try {
      const shell = buildDesignShell(ST.concept);
      pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
      pg.shellStatus = 'done';
      renderShellRow();
      updSectsCtx();
      pg.edited = false;
      pg.editHist = [];
      pg.editUndoStack = [];
      pg.assembled = assembledForPage(pg);
      writeFrame('ifr-build', pg.assembled);
    } catch (e) {
      pg.shellStatus = 'error';
      ST.running = false;
      renderShellRow();
      renderSbFoot();
      updSectsCtx();
      renderChat();
      showErr(`Shell failed: ${e.message}`);
      return;
    }
  }
  for (let i = 0; i < pg.sects.length; i++) {
    if (!ST.running) {
      break;
    }
    // Skip locked header/footer on non-first pages (synced from page 0)
    const sect = pg.sects[i];
    if (pgIdx > 0 && (sect.type === 'header' || sect.type === 'footer')) {
      continue;
    }
    if (['pending', 'error'].includes(sect.status)) {
      try {
        await buildSect(i, pg);
      } catch (e) {
        console.error('Section build error:', e);
        break;
      }
    }
  }
  ST.running = false;
  renderSbFoot();
  updSectsCtx();
  renderChat();
}

function buildOne(idx) {
  requireKey(() => {
    void buildOneWithShell(idx);
  });
}

function regenSect(idx) {
  const s = ST.sects[idx];
  // Track edit instruction history for this section
  if (s.note?.trim()) {
    s.editLog ??= [];
    s.editLog.push({ instr: s.note.trim(), applied: '' });
    s.note = '';
  }
  s.status = 'pending';
  s.html = '';
  s.screenshotCache = null;
  s.suggestions = null;
  requireKey(() => {
    void buildOneWithShell(idx);
  });
}

async function buildOneWithShell(idx) {
  if (ST.running) {
    return;
  }

  await skillsReady;

  const pg = curPage();
  if (pg.shellStatus !== 'done') {
    pg.shellStatus = 'building';
    renderShellRow();
    try {
      const shell = buildDesignShell(ST.concept);
      pg.shellHtml = `${shell.head}\n<!-- SF_SECTIONS -->\n${shell.foot}`;
      pg.shellStatus = 'done';
      renderShellRow();
    } catch (e) {
      pg.shellStatus = 'error';
      renderShellRow();
      showErr(`Shell failed: ${e.message}`);
      return;
    }
  }
  ST.running = true;
  renderSbFoot();
  renderChat();
  try {
    await buildSect(idx, pg);
  } catch (e) {
    console.error('Section build error:', e);
  }
  ST.running = false;
  renderSbFoot();
  renderChat();
}

/**
 * Build a single section by index — streaming AI generation with live preview.
 * @param {number} idx
 * @param {import('../types.ts').Page} [pg]
 * @param {{ previewFrame?: string, tempNote?: string, onSectionDone?: (s: import('../types.ts').Section) => void, skipStatusRender?: boolean }} [opts]
 * @returns {Promise<void>}
 */
async function buildSect(idx, pg, opts) {
  opts = opts || {};
  pg = pg || curPage();
  const s = pg.sects[idx];
  if (!s) {
    return;
  }
  s.status = 'building';
  if (!opts.skipStatusRender) {
    renderSList();
    updSectsCtx();
  }
  const c = ST.concept;
  const sk = skill('section');
  // Inject skills into system prompt — cached per section type since skills are static
  if (!ST.cachedSkillCtx) {
    ST.cachedSkillCtx = {};
  }
  if (!(s.type in ST.cachedSkillCtx)) {
    ST.cachedSkillCtx[s.type] = getSkillsForSection(s.type);
  }
  const skillCtx = ST.cachedSkillCtx[s.type];
  const sys = `${skillCtx ? `${skillCtx}\n\n` : ''}${sk.prompt || ''}`;
  const contextTxt = ST.conceptContext || briefTxt() || '(No brief provided)';

  // Build token context — cached since concept doesn't change during a build
  if (!ST.cachedTokenCtx) {
    ST.cachedTokenCtx = buildTokenContext(c);
  }
  const tokenCtx = ST.cachedTokenCtx;

  // Anthropic best practice: longform data at top, queries/instructions at end.
  // Brief content first (highest priority), then tokens, then section metadata.
  let user = `<brief>
${contextTxt}
</brief>

${tokenCtx}

<brand>
Site: ${c.siteName}
Headline: ${c.heroHeadline}
Subheadline: ${c.heroSubheadline || ''}
CTA: ${c.ctaText}
Secondary CTA: ${c.secondaryCta || ''}
Voice: ${c.brandVoiceSample}
Strategy: ${c.strategy || ''}
</brand>

<design_vision>
Concept: ${c.designVision?.concept || ''}
Page flow: ${c.designVision?.pageFlow || ''}
Signature element: ${c.designVision?.signatureElement || ''}
</design_vision>

<section_spec>
ID: ${s.id}
Type: ${s.type}
Title: ${s.title}
Purpose: ${s.purpose}
copyNotes: ${s.copyNotes || 'Write compelling copy based on the brief.'}
Visual brief: ${s.visualBrief || ''}
Layout: ${s.layout || 'auto'}
bgStyle: ${s.bgStyle || 'light'}${s.videoEmbed ? `\nvideoEmbed: ${JSON.stringify(s.videoEmbed)}` : ''}
</section_spec>`;

  // Inject type-specific build rules (only the relevant type, not all)
  const typeRule = SECTION_TYPE_RULES[s.type] || SECTION_TYPE_RULES._default;
  user += `\n\n<section_type_rules>\n${typeRule}\n</section_type_rules>`;

  // For header/footer, include section IDs (for anchor links) and other pages (for page links)
  if (s.type === 'header' || s.type === 'footer') {
    const siblings = pg.sects.filter(x => x.id !== s.id);
    user += `\n\n<page_sections>\n${siblings
      .map(x => `  #${x.id} — ${x.type}: ${x.title}`)
      .join('\n')}\n</page_sections>`;

    // Include other pages so Claude can link to them correctly
    if (ST.pages.length > 1) {
      const pgIdx = ST.pages.indexOf(pg);
      const otherPages = ST.pages
        .map((p, i) => {
          if (i === pgIdx) {
            return null;
          }
          const slug = p.name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
          const href = i === 0 ? '/' : `/${slug}`;
          return `  ${href} — ${p.name}`;
        })
        .filter(Boolean);
      if (otherPages.length) {
        user += `\n\n<other_pages>\n${otherPages.join('\n')}\n</other_pages>`;
      }
    }
  }

  // Edit history: accumulated directions from previous rebuilds
  const prevEdits = s.editLog || [];
  const currentDirection = opts.tempNote || s.note || '';
  if (prevEdits.length || currentDirection) {
    const parts = [];
    if (prevEdits.length) {
      parts.push('<edit_history>');
      prevEdits.forEach((h, i) =>
        parts.push(`${i + 1}. "${h.instr}" — applied${h.applied ? ` (${h.applied})` : ''}`),
      );
      if (currentDirection) {
        parts.push(`${prevEdits.length + 1}. "${currentDirection}" — current request`);
      }
      parts.push('</edit_history>');
      parts.push('Preserve ALL changes from previous edits. Apply only the current request.');
    }
    if (currentDirection && !prevEdits.length) {
      parts.push(`<user_direction>${currentDirection}</user_direction>`);
    }
    user += `\n\n${parts.join('\n')}`;
  }
  // Page position context — helps sections create visual transitions with neighbors
  const allSects = pg.sects;
  const sIdx = allSects.indexOf(s);
  const prev = sIdx > 0 ? allSects[sIdx - 1] : null;
  const next = sIdx < allSects.length - 1 ? allSects[sIdx + 1] : null;
  user += `\n\n<page_position>
Section ${sIdx + 1} of ${allSects.length}${prev ? `\nPrevious: #${prev.id} (${prev.type}, ${prev.bgStyle})${prev.visualBrief ? `\nPrevious visual: ${prev.visualBrief}` : ''}` : ''}${next ? `\nNext: #${next.id} (${next.type}, ${next.bgStyle})` : ''}
</page_position>`;

  const builtSiblings = allSects.filter(
    x => x.id !== s.id && x.type === s.type && x.status === 'done',
  );
  if (builtSiblings.length) {
    const sibInfo = builtSiblings
      .map(x => {
        const text = stripHtmlToText(x.html, 200);
        return `- #${x.id} "${x.title}": ${text || '(no content)'}`;
      })
      .join('\n');
    user += `\n\n<sibling_awareness>
This page already has ${builtSiblings.length} other ${s.type} section(s). Create DIFFERENT content — do NOT repeat the same headlines, copy, or layout patterns:
${sibInfo}
</sibling_awareness>`;
  }

  if (s.replaceImg) {
    user += `\nA replacement image has been provided. Use this URL as the image src: __REPLACE_IMG_${s.id}__`;
  }
  if (s.screenshotImg) {
    user +=
      '\nA screenshot has been provided as a layout reference \u2014 match its visual structure and composition as closely as possible.';
  }

  // Inject existing-site mode prompts — only for home page sections
  const isHomePage = pg.id === 'page-home';
  if (isHomePage && ST.startPath === 'existing' && ST.existingOpt) {
    user += `\n\n${getSectionModePrompt(ST.existingOpt, s, ST.existingScrape)}`;
  }

  // Image context per page type
  if (!isHomePage) {
    // Non-home pages: reference images + page-level images + stock/logo fallback
    const refImages = s.referenceImages || [];
    const pageImages = pg.pageImages || [];
    const allRefImgs = [
      ...refImages.map(img => `<img url="${img.url}" alt="${img.alt}" />`),
      ...pageImages
        .filter(img => !refImages.some(ref => ref.url === img.src))
        .map(img => `<img url="${img.src}" alt="${img.alt}" category="${img.category}" />`),
    ];
    if (allRefImgs.length) {
      user += `\n\n<available_images>\n${allRefImgs.join('\n')}\n</available_images>`;
    } else {
      // No reference images — fall back to stock images + logo (same as home non-keep)
      user += `\n\n${buildImageContext(c, s.id)}`;
    }
  } else if (ST.startPath === 'existing' && ST.existingOpt === 'keep') {
    // Home keep mode: only provide the logo (content_lock has section-specific images)
    const logoSrc = resolveLogoSrc();
    if (logoSrc) {
      user += `\n\n<available_images>\n<logo url="${logoSrc}" alt="${c.siteName || 'Logo'}" />\n</available_images>`;
    }
  } else {
    // Home non-keep: per-section image context with stock images
    user += `\n\n${buildImageContext(c, s.id)}`;
  }
  // Business context always needed (phone, copyright, social links)
  if (!ST.cachedBusinessCtx) {
    ST.cachedBusinessCtx = buildBusinessContext();
  }
  user += ST.cachedBusinessCtx;

  // Legal links for contact/subscribe sections (SMS consent requirement)
  if (s.type === 'contact' || s.type === 'subscribe') {
    const legal = ST.existingScrape?.legalLinks;
    if (legal) {
      user += '\n\n<legal_links>';
      if (legal.privacyPolicy) {
        user += `\nPrivacy Policy: ${legal.privacyPolicy.url}`;
      }
      if (legal.termsOfService) {
        user += `\nTerms of Service: ${legal.termsOfService.url}`;
      }
      user += '\n</legal_links>';
    }
  }

  // Build placeholder map so streaming preview resolves uploaded image URLs
  ST.uploadImgMap = buildUploadImgMap();

  const sectImgs = [];
  if (s.replaceImg) {
    sectImgs.push({
      ...s.replaceImg,
      label: 'User-provided replacement image — use as the primary visual for this section',
    });
  }
  if (s.screenshotImg) {
    sectImgs.push({
      ...s.screenshotImg,
      label: 'Screenshot layout reference — match structure and composition, NOT visual style',
    });
  }
  // Pass logo as vision input for header/footer sections (uploaded, URL, or scraped)
  if (s.type === 'header' || s.type === 'footer') {
    const logoVision = getLogoVisionInput(
      'Company logo \u2014 integrate gracefully into header/footer branding, ensure proper sizing and contrast',
    );
    if (logoVision) {
      sectImgs.push(logoVision);
    }
  }
  // In keep mode: pass full-page screenshot so AI understands the content STRUCTURE (not the visual style)
  // Only for home page sections — non-home pages have their own pageScreenshot
  if (
    isHomePage &&
    ST.startPath === 'existing' &&
    ST.existingOpt === 'keep' &&
    ST.siteScreenshot?.length
  ) {
    sectImgs.push(
      ...ST.siteScreenshot.map((tile, i) => ({
        ...tile,
        label:
          i === 0
            ? 'Full-page screenshot of the original website — layout reference only, apply new design tokens for style'
            : `(screenshot continuation tile ${i + 1})`,
      })),
    );
    user +=
      '\n\n<screenshot_reference>A screenshot of the original website is provided. Use it to understand content structure — what is a card grid, what is a hero split, where images are positioned relative to text. Apply the new design tokens for visual style (colors, fonts, spacing, decorative elements). The screenshot informs layout; the design tokens define the new look.</screenshot_reference>';
  }
  // Pass uploaded images whose placement matches this section type
  if (ST.uploads?.images?.length) {
    ST.uploads.images
      .filter(i => i.analysis?.useDecision === 'use')
      .filter(i => {
        const p = (i.analysis.placement || '').toLowerCase();
        return p.includes(s.type) || p.includes(s.id);
      })
      .slice(0, 2)
      .forEach(img =>
        sectImgs.push({ ...img, label: `User-uploaded image: ${img.name || 'unnamed'}` }),
      );
  }

  // ── Collect available images as vision blocks for contrast-aware generation ──
  let HasBgImages = false;
  const bgSectionTypes = ['hero', 'cta', 'about', 'header', 'content'];
  if (bgSectionTypes.includes(s.type) || s.bgStyle === 'image') {
    const sectionVisionImgs = await collectSectionImages(s, {
      pg,
      maxImages: s.type === 'header' ? 2 : 3,
    });
    if (sectionVisionImgs.length) {
      sectImgs.push(...sectionVisionImgs);
      HasBgImages = true;
    }
  }

  // Image awareness: only when section images are present in vision
  if (HasBgImages) {
    user += `\n\n<image_awareness>
Available images for this section have been provided as labeled vision inputs.
You can now SEE each image. Use visual analysis to make informed decisions:

- Before placing text over an image, analyze its brightness and visual complexity in the text region
- Determine overlay opacity from the actual image — don't default to a fixed value
- The TOP region is critical: headers overlap hero sections, so the top 80px must have strong contrast
- Dark/uniform images may need no overlay. Bright/busy images need multi-stop gradient overlay + text-shadow.
- If the image is a video poster, it represents the initial visual before playback — ensure readability against it
</image_awareness>`;
  }

  let latest = '';
  let lastWritten = '';
  const frameId = opts.previewFrame || 'ifr-build';
  const tick = setInterval(() => {
    if (latest && pg.shellHtml && latest !== lastWritten) {
      const safeHtml = safeStreamHtml(cleanModelOutput(latest));
      try {
        const tmp = pg.sects.map((x, i) => (i === idx ? { ...x, html: safeHtml } : x));
        patchFrame(
          frameId,
          pg.shellHtml.replace(
            '<!-- SF_SECTIONS -->',
            tmp
              .map(
                x =>
                  x.html ||
                  `<div id="${x.id}" style="padding:60px 20px;text-align:center;color:var(--text-muted);font-size:13px;font-family:var(--font-b);background:var(--bg-alt);border-bottom:1px solid var(--border)">${x.type}</div>`,
              )
              .join('\n'),
          ),
        );
        lastWritten = latest;
      } catch (_e) {
        // Partial HTML caused a parse error — skip, next tick will retry with more content
      }
    }
  }, 500);
  if (!ST.activeStreamTicks) {
    ST.activeStreamTicks = [];
  }
  ST.activeStreamTicks.push(tick);
  try {
    const tokens = sk.tokens || SECTION_TOKENS_DEFAULT;
    const html = await claude({
      sys,
      user,
      imgs: sectImgs,
      tokens,
      model: sk.model,
      temp: sk.temp,
      effort: sk.effort,
      onChunk(t) {
        latest = t;
      },
    });
    clearInterval(tick);
    ST.activeStreamTicks = ST.activeStreamTicks.filter(t => t !== tick);
    s.html = cleanModelOutput(html);
    if (s.type === 'contact' || s.type === 'subscribe' || s.html.includes('<form')) {
      s.html = injectFormMeta(s.html, s.type === 'subscribe' ? 'subscribe' : 'contact');
    }
    s.status = 'done';
    s.error = '';
    s.stale = false;
    s.screenshotCache = null;
    s.suggestions = null;
    pg.assembled = assembledForPage(pg);
    finalizeFrame(frameId, pg.assembled);
    // Invalidate home page screenshot cache when home content changes
    if (pg.id === 'page-home') {
      ST.homePageScreenshot = null;
    }
    // Auto-sync header/footer to other pages (skip during design preview)
    if (
      !opts.skipSync &&
      (s.type === 'header' || s.type === 'footer') &&
      ST.pages.indexOf(pg) === 0 &&
      ST.pages.length > 1
    ) {
      syncSharedSection(s);
    }
    if (opts.onSectionDone) {
      opts.onSectionDone(s);
    }
  } catch (e) {
    clearInterval(tick);
    if (ST.activeStreamTicks) {
      ST.activeStreamTicks = ST.activeStreamTicks.filter(t => t !== tick);
    }
    s.status = 'error';
    s.error = e.message;
    showErr(`Section "${s.title}" failed: ${e.message}`);
    throw e;
  } finally {
    if (!opts.skipStatusRender) {
      renderSList();
      updSectsCtx();
    }
  }
}

/**
 * Build all pages sequentially — home first, then others.
 * Home must complete before others (shared header/footer syncs to other pages).
 */
function buildAllPages() {
  requireKey(() => {
    void BuildAllPagesImpl();
  });
}

async function BuildAllPagesImpl() {
  // Build home page first (index 0)
  ST.activePage = 0;
  renderSList();
  updSectsCtx();
  renderSbFoot();
  await buildAll();

  // Then build each non-home page that has content sections
  for (let pageIndex = 1; pageIndex < ST.pages.length; pageIndex++) {
    const page = ST.pages[pageIndex];
    const hasContentSections = page.sects.some(
      s => s.type !== 'header' && s.type !== 'footer' && s.status === 'pending',
    );
    if (!hasContentSections) {
      continue;
    }

    ST.activePage = pageIndex;
    renderSList();
    updSectsCtx();
    renderSbFoot();
    if (curPage().assembled) {
      writeFrame('ifr-build', curPage().assembled);
    }
    await buildAll();
  }

  // Return to home page
  ST.activePage = 0;
  renderSList();
  updSectsCtx();
  renderSbFoot();
  if (curPage().assembled) {
    writeFrame('ifr-build', curPage().assembled);
  }
}

/**
 * Sync a shared section (header/footer) from page 0 to all other pages.
 * @param {import('../types.ts').Section} srcSect
 * @returns {void}
 */
function syncSharedSection(srcSect) {
  let synced = 0;
  for (let i = 1; i < ST.pages.length; i++) {
    const pg = ST.pages[i];
    const target = pg.sects.find(s => s.type === srcSect.type);
    if (target) {
      target.html = srcSect.html;
      target.status = 'done';
      target.error = '';
      target.screenshotCache = null;
      target.suggestions = null;
      pg.edited = false;
      pg.editHist = [];
      pg.editUndoStack = [];
      if (pg.shellHtml) {
        pg.assembled = assembledForPage(pg);
      }
      synced++;
    }
  }
  if (synced > 0) {
    showToast(
      `${srcSect.type.charAt(0).toUpperCase() + srcSect.type.slice(1)} synced to ${synced} page${synced > 1 ? 's' : ''}`,
    );
  }
}

// ── MODULE EXPORTS ──────
export {
  buildAll,
  buildAllPages,
  buildOne,
  buildOneWithShell,
  buildSect,
  regenSect,
  startBuildAll,
  syncSharedSection,
};
